import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../components';
import { AppAvatar } from '../app-shell-core';
import { WorkspaceStatusIcon } from '../design-system/workspace-primitives';
import { appointmentDurationMinutes, appointmentFlowProgress, dashboardVisitFlowLabel, dashboardVisitFlowTone } from './dashboard-model';


function patientAge(dateOfBirth?: string | null): number | null {
  if (!dateOfBirth) return null;
  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDelta = today.getMonth() - dob.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < dob.getDate())) age -= 1;
  return Math.max(0, age);
}

function patientDobContactLabel(patient: any) {
  const age = patientAge(patient?.dateOfBirth);
  const dob = patient?.dateOfBirth
    ? new Date(patient.dateOfBirth).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
    : 'DOB not on file';
  return { dob: age === null ? dob : `${dob} (${age})`, contact: patient?.phone || patient?.email || '—' };
}

function DashboardVisitFlowControl({ appointment, disabled, open, currentTimeMs, onToggle, onChange }: { appointment: any; disabled: boolean; open: boolean; currentTimeMs: number; onToggle: (open: boolean) => void; onChange: (status: string) => void }) {
  const statuses = appointment.status === 'CONFIRMED'
    ? ['CONFIRMED', 'CHECKED_IN', 'IN_PROGRESS', 'COMPLETED', 'NO_SHOW', 'CANCELED']
    : ['SCHEDULED', 'CHECKED_IN', 'IN_PROGRESS', 'COMPLETED', 'NO_SHOW', 'CANCELED'];
  const progress = appointmentFlowProgress(appointment, currentTimeMs);
  const tone = dashboardVisitFlowTone(appointment.status);
  const activeProgress = appointment.status === 'IN_PROGRESS' && progress.hasTiming;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({ visibility: 'hidden' });

  useLayoutEffect(() => {
    if (!open) return undefined;

    let frame = 0;
    const placeMenu = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      const menuHeight = menuRef.current?.offsetHeight || 276;
      const menuWidth = 216;
      const margin = 8;
      const gap = 6;
      const roomBelow = window.innerHeight - rect.bottom - gap - margin;
      const roomAbove = rect.top - gap - margin;
      const openAbove = roomBelow < Math.min(menuHeight, 240) && roomAbove > roomBelow;
      const availableHeight = Math.max(132, openAbove ? roomAbove : roomBelow);
      const top = openAbove
        ? Math.max(margin, rect.top - gap - Math.min(menuHeight, availableHeight))
        : rect.bottom + gap;
      const maxLeft = Math.max(margin, window.innerWidth - menuWidth - margin);
      const left = Math.min(Math.max(margin, rect.right - menuWidth), maxLeft);

      setMenuStyle({
        top,
        left,
        width: menuWidth,
        maxHeight: availableHeight,
        visibility: 'visible',
      });
    };

    placeMenu();
    frame = window.requestAnimationFrame(placeMenu);
    window.addEventListener('resize', placeMenu);
    window.addEventListener('scroll', placeMenu, true);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', placeMenu);
      window.removeEventListener('scroll', placeMenu, true);
    };
  }, [open]);

  const menu = open && typeof document !== 'undefined'
    ? createPortal(
      <div ref={menuRef} className="nds-dashboard-flow-menu" role="menu" style={menuStyle} onClick={(event) => event.stopPropagation()}>
        {statuses.map((status) => {
          const selected = status === appointment.status;
          return <button type="button" role="menuitemradio" aria-checked={selected} key={status} className={selected ? 'active' : ''} disabled={disabled} onClick={(event) => { event.stopPropagation(); onToggle(false); onChange(status); }}>
            <span className={`nds-dashboard-flow-dot is-${dashboardVisitFlowTone(status)}`} />
            <span>{dashboardVisitFlowLabel(status)}</span>
            {selected ? <span className="nds-dashboard-flow-selected" aria-hidden="true"><Icon name="check" /></span> : null}
          </button>;
        })}
      </div>,
      document.body,
    )
    : null;

  return <div className={`nds-dashboard-flow-control is-${tone} ${progress.isOvertime ? 'is-overtime' : ''} ${open ? 'is-open' : ''}`} onClick={(event) => event.stopPropagation()}>
    <button
      ref={triggerRef}
      type="button"
      className="nds-dashboard-flow-trigger"
      style={activeProgress ? ({ '--nds-flow-progress': `${progress.percent}%` } as CSSProperties) : undefined}
      aria-label={progress.ariaLabel}
      title={progress.ariaLabel}
      aria-expanded={open}
      aria-haspopup="menu"
      onClick={(event) => { event.stopPropagation(); onToggle(!open); }}
    >
      {activeProgress ? <span className="nds-dashboard-flow-progress" aria-hidden="true" /> : null}
      <span className="nds-dashboard-flow-dot" aria-hidden="true" />
      <span className="nds-dashboard-flow-copy"><strong>{dashboardVisitFlowLabel(appointment.status)}</strong>{progress.hasTiming && progress.detailLabel ? <small>{progress.detailLabel}</small> : null}</span>
    </button>
    {menu}
  </div>;
}

export function DashboardSchedule({ appointments, canView, canCreate, canEdit, currentTimeMs, flowBusyId, openFlowId, onOpenAppointment, onCreate, onToggleFlow, onChangeFlow }: { appointments: any[]; organizationId: string; canView: boolean; canCreate: boolean; canEdit: boolean; currentTimeMs: number; flowBusyId: string; openFlowId: string; onOpenAppointment: (appointment: any) => void; onCreate: () => void; onToggleFlow: (id: string) => void; onChangeFlow: (appointment: any, status: string) => void }) {
  if (!canView) return <div className="nds-dashboard-empty"><WorkspaceStatusIcon icon="lock" label="Schedule access restricted" /><h3>Schedule access restricted</h3><p>Your role does not include appointment schedule access.</p></div>;
  if (!appointments.length) return <div className="nds-dashboard-empty"><WorkspaceStatusIcon icon="calendar-check" label="Schedule clear" tone="success" /><h3>Your schedule is clear</h3><p>No appointments are scheduled for today.</p>{canCreate && <button type="button" className="button primary" onClick={onCreate}>Schedule appointment</button>}</div>;

  return <div className="nds-dashboard-schedule">
    <div className="nds-dashboard-schedule-head" role="row"><span>Time</span><span>Patient</span><span>DOB / Contact</span><span>Visit type</span><span>Provider</span><span>Visit flow</span></div>
    <div className="nds-dashboard-schedule-rows">{appointments.map((appointment) => {
      const patientName = `${appointment.patient.preferredName || appointment.patient.firstName} ${appointment.patient.lastName}`;
      const providerName = appointment.providerMembership ? `${appointment.providerMembership.user.firstName} ${appointment.providerMembership.user.lastName}` : 'Unassigned';
      const patientContact = patientDobContactLabel(appointment.patient);
      const progress = appointmentFlowProgress(appointment, currentTimeMs);
      const flowTone = dashboardVisitFlowTone(appointment.status);
      const activeProgress = appointment.status === 'IN_PROGRESS' && progress.hasTiming;
      return <div className={`nds-dashboard-appointment-row ${openFlowId === appointment.id ? 'has-open-flow' : ''}`} key={appointment.id} role="button" tabIndex={0} onClick={(event) => { if ((event.target as HTMLElement).closest('.nds-dashboard-flow-control')) return; onOpenAppointment(appointment); }} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onOpenAppointment(appointment); } }}>
        <time className="nds-dashboard-time" dateTime={appointment.startsAt}><strong>{new Date(appointment.startsAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</strong><small>{appointmentDurationMinutes(appointment)} min</small></time>
        <span className="nds-dashboard-patient"><AppAvatar name={patientName} identityKey={appointment.patient.id} size="sm" /><span><strong title={patientName}>{patientName}</strong><small>{`MRN ${appointment.patient.patientNumber || 'Not assigned'}`}</small></span></span>
        <span className="nds-dashboard-cell is-contact"><strong>{patientContact.dob}</strong><small>{patientContact.contact}</small></span>
        <span className="nds-dashboard-cell is-visit"><strong title={appointment.serviceType.name}>{appointment.serviceType.name}</strong><small>{appointment.location?.code || appointment.location?.name || 'Location not assigned'}</small></span>
        <span className="nds-dashboard-cell is-provider"><strong title={providerName}>{providerName}</strong><small>{appointment.providerMembership?.providerProfile?.credentials || 'Provider'}</small></span>
        <div className="nds-dashboard-flow-cell">{canEdit ? <DashboardVisitFlowControl appointment={appointment} disabled={flowBusyId === appointment.id} open={openFlowId === appointment.id} currentTimeMs={currentTimeMs} onToggle={(nextOpen) => onToggleFlow(nextOpen ? appointment.id : '')} onChange={(status) => onChangeFlow(appointment, status)} /> : <span className={`nds-dashboard-flow-readonly is-${flowTone} ${progress.isOvertime ? 'is-overtime' : ''}`} title={progress.ariaLabel}><span className="nds-dashboard-flow-dot" /><span className="nds-dashboard-flow-copy"><strong>{dashboardVisitFlowLabel(appointment.status)}</strong>{progress.hasTiming && progress.detailLabel ? <small>{progress.detailLabel}</small> : null}</span></span>}</div>
      </div>;
    })}</div>
    {canCreate && <button type="button" className="nds-dashboard-schedule-add" onClick={onCreate}><Icon name="calendar-plus" />Schedule another appointment</button>}
  </div>;
}
